import { useState } from "react";

import {
  useEdit,
  useTaxonomy,
  type EditRequest,
  type QuestionDetail,
  type TaxonomyEntryOut,
} from "@/api/queries";
import { Button } from "@/components/ui/button";
import { Combobox, FieldLabel, Input, Textarea, type ComboboxOption } from "@/components/ui/input";
import { Callout, ErrorNote } from "@/components/ui/panel";

/**
 * Concepts suggested for the form's current stack, same prefix idea as the
 * enrichment prompt's concept_hint (providers/openai_compatible.py) — falls back to
 * every known concept when the stack has none yet, so scoping only narrows, never
 * blocks: `allowCustomValue` on the Combobox already lets a reviewer type past it.
 */
function conceptOptions(concepts: TaxonomyEntryOut[], stack: string): ComboboxOption[] {
  const scoped = stack
    ? concepts.filter((c) => c.path === stack || c.path.startsWith(`${stack}.`))
    : concepts;
  const pool = scoped.length > 0 ? scoped : concepts;
  return pool.map((c) => ({ value: c.path, label: c.path }));
}

/**
 * Every field `EditRequest` accepts, in one form. A field is only put on the patch
 * when it differs from the row already on screen — the same no-op detection
 * `review.edit` itself does — so touching nothing sends nothing, and touching one
 * field never resends the others' current values as spurious "changes" (§7.5).
 */
export function QuestionEditForm({
  q,
  onCancel,
  onSaved,
}: {
  q: QuestionDetail;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const { data: stacks } = useTaxonomy("stack");
  const { data: concepts } = useTaxonomy("concept");
  const edit = useEdit();

  const [text, setText] = useState(q.question_text);
  const [answer, setAnswer] = useState(q.model_answer ?? "");
  const [stack, setStack] = useState(q.stack_path ?? stacks?.[0]?.path ?? "");
  const [concept, setConcept] = useState(q.primary_concept ?? "");
  const [entities, setEntities] = useState(q.entities.join(", "));
  const [versionMin, setVersionMin] = useState(q.version_min ?? "");
  const [versionMax, setVersionMax] = useState(q.version_max ?? "");
  const [signature, setSignature] = useState(q.concept_signature ?? "");
  const [note, setNote] = useState("");

  const textBlank = text.trim().length === 0;

  function save() {
    if (textBlank) return;
    const patch: EditRequest = {};
    if (text.trim() !== q.question_text) patch.question_text = text;
    const ans = answer.trim();
    if (ans !== (q.model_answer ?? "")) patch.model_answer = ans;
    if (stack && stack !== q.stack_path) patch.stack_path = stack;
    const c = concept.trim();
    if (c && c !== q.primary_concept) patch.primary_concept = c;
    const parsedEntities = entities
      .split(",")
      .map((e) => e.trim())
      .filter(Boolean);
    if (parsedEntities.join(",") !== q.entities.join(",")) patch.entities = parsedEntities;
    const vMin = versionMin.trim();
    if (vMin !== (q.version_min ?? "")) patch.version_min = vMin;
    const vMax = versionMax.trim();
    if (vMax !== (q.version_max ?? "")) patch.version_max = vMax;
    // Mirrors review.edit's own `" ".join(concept_signature.split())` collapse.
    const sigJoined = signature.trim().split(/\s+/).filter(Boolean).join(" ");
    if (sigJoined !== (q.concept_signature ?? "")) patch.concept_signature = sigJoined;

    if (Object.keys(patch).length === 0) {
      onCancel();
      return;
    }
    patch.note = note.trim() || "edited by reviewer";
    edit.mutate({ id: q.id, patch }, { onSuccess: onSaved });
  }

  return (
    <div className="space-y-3.5">
      <div>
        <FieldLabel htmlFor="edit-text">Question text</FieldLabel>
        <Textarea id="edit-text" value={text} onChange={(e) => setText(e.target.value)} />
        {textBlank && <p className="text-bank-bad-ink mt-1 text-xs">Question text cannot be blank.</p>}
      </div>

      <div>
        <FieldLabel htmlFor="edit-answer">Model answer</FieldLabel>
        <Textarea id="edit-answer" value={answer} onChange={(e) => setAnswer(e.target.value)} />
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <FieldLabel htmlFor="edit-stack">Stack</FieldLabel>
          <Combobox
            id="edit-stack"
            value={stack}
            onChange={setStack}
            options={(stacks ?? []).map((s) => ({ value: s.path, label: s.path }))}
          />
        </div>
        <div>
          <FieldLabel htmlFor="edit-concept">Primary concept</FieldLabel>
          <Combobox
            id="edit-concept"
            value={concept}
            onChange={setConcept}
            allowCustomValue
            options={conceptOptions(concepts ?? [], stack)}
          />
        </div>
      </div>

      <div>
        <FieldLabel htmlFor="edit-entities">Entities — comma separated</FieldLabel>
        <Input
          id="edit-entities"
          value={entities}
          onChange={(e) => setEntities(e.target.value)}
          placeholder="var, let"
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <FieldLabel htmlFor="edit-vmin">Version min</FieldLabel>
          <Input id="edit-vmin" value={versionMin} onChange={(e) => setVersionMin(e.target.value)} />
        </div>
        <div>
          <FieldLabel htmlFor="edit-vmax">Version max</FieldLabel>
          <Input id="edit-vmax" value={versionMax} onChange={(e) => setVersionMax(e.target.value)} />
        </div>
      </div>

      <div>
        <FieldLabel htmlFor="edit-signature">Signature — this is what gets embedded</FieldLabel>
        <Textarea id="edit-signature" value={signature} onChange={(e) => setSignature(e.target.value)} />
        <Callout className="mt-1.5">
          Changing this invalidates the embedding — the question goes back to "enriched" for re-embedding.
        </Callout>
      </div>

      <div>
        <FieldLabel htmlFor="edit-note">Note — recorded on the status_transitions row</FieldLabel>
        <Input
          id="edit-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="why, for whoever reads this in six months"
        />
      </div>

      <div className="flex gap-1.5">
        <Button size="sm" variant="primary" disabled={edit.isPending || textBlank} onClick={save}>
          {edit.isPending ? "Saving…" : "Save"}
        </Button>
        <Button size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
      <ErrorNote error={edit.error} />
    </div>
  );
}
